import { CheckCircle, XCircle } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { formatPercentage } from '@/utils/formatters';

interface VersionComplianceProps {
  compliant: number;
  outdated: number;
  latestVersion: string | null;
  totalNodes: number;
  onSelect?: (type: 'compliant' | 'outdated' | 'version', value: string) => void;
}

export function VersionCompliance({
  compliant,
  outdated,
  latestVersion,
  totalNodes,
  onSelect,
}: VersionComplianceProps) {
  const compliancePercent = totalNodes > 0 ? (compliant / totalNodes) * 100 : 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          Version Compliance
          <InfoTooltip text="Percentage of Nodes running the latest software version. Outdated Nodes may need a firmware upgrade." />
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-space-y-4">
        {latestVersion && (
          <p
            className={`dls-text-sm dls-text-muted-foreground ${onSelect ? 'dls-cursor-pointer hover:dls-underline' : ''}`}
            onClick={() => onSelect && onSelect('version', latestVersion)}
          >
            Latest version: <span className="dls-font-mono dls-font-medium">{latestVersion}</span>
          </p>
        )}

        <div className="dls-flex dls-items-center dls-justify-between">
          <div
            className={`dls-flex dls-items-center dls-gap-2 dls-text-status-online ${onSelect ? 'cursor-pointer hover:opacity-80' : ''}`}
            onClick={() => latestVersion && onSelect && onSelect('version', latestVersion)}
          >
            <CheckCircle className="dls-h-5 dls-w-5" />
            <span className="dls-font-medium">{compliant} up to date</span>
          </div>
          <span className="dls-text-2xl dls-font-bold dls-text-status-online">
            {formatPercentage(compliancePercent, 0)}
          </span>
        </div>

        {outdated > 0 && (
          <div
            className={`dls-flex dls-items-center dls-gap-2 dls-text-status-warning ${onSelect ? 'cursor-pointer hover:opacity-80' : ''}`}
            onClick={() => onSelect && onSelect('outdated', 'outdated')}
          >
            <XCircle className="dls-h-5 dls-w-5" />
            <span className="dls-font-medium">{outdated} need upgrade</span>
          </div>
        )}

        <div className="dls-w-full dls-bg-muted dls-rounded-full dls-h-2">
          <div
            className="dls-bg-status-online dls-h-2 dls-rounded-full dls-transition-all"
            style={{ width: `${compliancePercent}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
